import { SETTINGS, BREAKPOINTS, CLASSES, ARIAS } from '../../utils/constants';
import initSearch from './search';

const MEGAMENU_PANEL_FOCUSABLE_SELECTOR = 'a[href], button:not([disabled])';

const initHeader = () => {
  const headerEl = document.querySelector('[data-header]');
  if (!headerEl) return;
  const desktopMediaQuery = window.matchMedia(`(min-width: ${BREAKPOINTS.DESKTOP}px)`);
  const navToggle = headerEl.querySelector('[data-nav-toggle]');
  const navToggleText = navToggle.querySelector('[data-nav-toggle-text]');
  const navigationEl = headerEl.querySelector('[data-navigation]');
  const navigationWrapperEl = navigationEl.querySelector('[data-navigation-wrapper]');
  const navMenuEls = navigationEl.querySelectorAll('[data-navigation-menu]');
  const navigationBanners = navigationEl.querySelectorAll('[data-navigation-banners]');
  const navigationCTAs = navigationEl.querySelector('[data-navigation-ctas]');
  const childNavEls = headerEl.querySelectorAll('[data-navigation-index]');
  const desktopParentLinkEls = headerEl.querySelectorAll('[data-navigation-parent-dt]');
  const childNavDtLinkEls = headerEl.querySelectorAll('[data-child-nav-dt-item]');
  const announcementBar = document.querySelector('[data-announcement-bar]');
  const headerHadWhiteBackground = headerEl.classList.contains(CLASSES.BG_WHITE);
  const announcementBarHadWhiteBackground = announcementBar?.classList.contains(CLASSES.BG_WHITE) ?? false;

  const initMobNavigationTabs = () => {
    const navTabs = headerEl.querySelectorAll('[data-navigation-parent]');
    const menuEls = headerEl.querySelectorAll('[data-navigation-index]');
    if (!navTabs.length || !menuEls.length) return;

    const changeTab = (navTabs, currentTab, currentTabIndex, menuEls) => {
      navTabs.forEach((tab) => {
        tab.classList.remove('navigation-item-active');
        tab.setAttribute(ARIAS.EXPANDED, false);
      });
      currentTab.classList.add('navigation-item-active');
      currentTab.setAttribute(ARIAS.EXPANDED, true);

      menuEls.forEach((content) => {
        content.classList.add('hidden');
        content.setAttribute(ARIAS.EXPANDED, false);
      });
      menuEls[currentTabIndex].classList.remove('hidden');
      menuEls[currentTabIndex].setAttribute(ARIAS.EXPANDED, true);

      if (currentTab.dataset.navigationHasCtaCards === 'true') {
        navigationBanners?.forEach(banner => banner.classList.add('hidden'));
        navigationCTAs?.classList.add('hidden');
      } else {
        navigationBanners?.forEach(banner => banner.classList.remove('hidden'));
        navigationCTAs?.classList.remove('hidden');
      }
    };

    navTabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        changeTab(navTabs, tab, index, menuEls);
      });
    });
  };

  const closeSubNav = (subNav, level, closeBtn, handler) => {
    subNav.classList.remove(CLASSES.TRANSLATE_X_0);
    if (level === '2') {
      navigationWrapperEl.classList.remove(CLASSES.OVERFLOW_HIDDEN);
    } else {
      const parentMenu = subNav.closest('[data-navigation-menu="2"]');
      subNav.classList.remove(CLASSES.SCROLLABLE);
      if (parentMenu) {
        parentMenu.classList.remove(CLASSES.OVERFLOW_HIDDEN);
      }
    }
    closeBtn.removeEventListener('click', handler);
  };

  const openSubNav = (subNav, level) => {
    if (!subNav) return;
    subNav.classList.add(CLASSES.TRANSLATE_X_0);
    navigationWrapperEl.classList.add(CLASSES.OVERFLOW_HIDDEN);
    if (level === '3') {
      const parentMenu = subNav.closest('[data-navigation-menu="2"]');
      if (parentMenu) {
        parentMenu.scrollTop = 0;
        subNav.classList.add(CLASSES.SCROLLABLE);
        parentMenu.classList.add(CLASSES.OVERFLOW_HIDDEN);
      }
    }

    const closeBtn = subNav.querySelector('[data-navigation-close]');
    if (closeBtn) {
      const handleClose = () => closeSubNav(subNav, level, closeBtn, handleClose);
      closeBtn.addEventListener('click', handleClose);
    }
  };

  const bindNavigationLinks = (links) => {
    links.forEach(link => {
      link.addEventListener('click', (evt) => {
        if (desktopMediaQuery.matches) return;

        const subNav = link.nextElementSibling;
        if (!subNav || !subNav.dataset.navigationMenu) return;

        evt.preventDefault();
        openSubNav(subNav, subNav.dataset.navigationMenu);
      }, { capture: true });
    });
  };

  const resetNavigation = () => {
    navMenuEls.forEach(menu => {
      menu.classList.remove(CLASSES.TRANSLATE_X_0);
      menu.scrollTop = 0;
    });
    navigationEl.scrollTop = 0;
    navigationWrapperEl.classList.remove(CLASSES.OVERFLOW_HIDDEN);
  };

  const initDesktopNav = () => {
    if (!desktopParentLinkEls.length) return;

    let handleOutside = null;
    let handleEscapeKey = null;
    let openTimer = null;
    let activeTrigger = null;

    const removeGlobalHandlers = () => {
      if (handleOutside) {
        document.removeEventListener('mouseover', handleOutside);
        document.removeEventListener('focusin', handleOutside);
        handleOutside = null;
      }
      if (handleEscapeKey) {
        document.removeEventListener('keydown', handleEscapeKey);
        handleEscapeKey = null;
      }
    };

    const toggleMegaMenu = (show, activeLink = null, activePanel = null) => {
      if (openTimer) {
        clearTimeout(openTimer);
        openTimer = null;
      }

      if (show) {
        removeGlobalHandlers();
        activeTrigger = activeLink;
        navigationEl.classList.remove('lg:hidden');
        openTimer = setTimeout(() => {
          headerEl?.parentElement.classList.add('nav-dt-active');
          navigationEl?.classList.remove('lg:opacity-0');
          openTimer = null;
        }, SETTINGS.MEGAMENU_THRESHOLD);
        window.theme?.search?.clearSearch();

        // Reset all first
        desktopParentLinkEls.forEach(link => link.setAttribute(ARIAS.EXPANDED, false));
        childNavEls.forEach(childLink => childLink.setAttribute(ARIAS.EXPANDED, false));

        // Set active
        activeLink?.setAttribute(ARIAS.EXPANDED, true);
        activePanel?.setAttribute(ARIAS.EXPANDED, true);

        handleOutside = (e) => {
          if (!e.target.closest('[data-header]')) toggleMegaMenu(false);
        };
        handleEscapeKey = (e) => {
          if (e.key !== 'Escape') return;
          // Return focus to the trigger when the menu was open via keyboard,
          // so focus is never lost to the body when the panel is hidden.
          const triggerToRefocus = activeTrigger;
          const focusWasInMenu = headerEl.contains(document.activeElement);
          toggleMegaMenu(false);
          if (focusWasInMenu) triggerToRefocus?.focus();
        };

        document.addEventListener('mouseover', handleOutside);
        document.addEventListener('focusin', handleOutside);
        document.addEventListener('keydown', handleEscapeKey);
      } else {
        navigationEl?.classList.add('lg:opacity-0');
        headerEl?.parentElement.classList.remove('nav-dt-active');
        navigationEl?.classList.add('lg:hidden');

        // Reset all aria
        desktopParentLinkEls.forEach(l => l.setAttribute(ARIAS.EXPANDED, false));
        childNavEls.forEach(p => p.setAttribute(ARIAS.EXPANDED, false));

        activeTrigger = null;
        removeGlobalHandlers();
      }
    };

    const toggleChildNav = (index) => {
      if (!childNavEls.length) return;
      childNavEls.forEach(nav => nav.classList.add('hidden'));
      childNavEls[index]?.classList.remove('hidden');
    };

    const toggleChildNavDt = (index) => {
      const childNavMenuEls = headerEl.querySelectorAll('[data-child-nav-dt-menu]');
      if (!childNavMenuEls.length) return;
      const megaMenuInitialItemEl = headerEl.querySelector('[data-megamenu-initial-item]');
      megaMenuInitialItemEl?.classList.add('hidden');
      childNavMenuEls.forEach(childNav => childNav.classList.add('hidden'));
      headerEl.querySelector(`[data-child-nav-dt-menu="${index}"]`)?.classList.remove('hidden');
    };

    const focusFirstPanelLink = (panel) => {
      if (!panel) return;
      const firstLink = [...panel.querySelectorAll(MEGAMENU_PANEL_FOCUSABLE_SELECTOR)]
        .find(el => el.getClientRects().length);
      firstLink?.focus();
    };

    desktopParentLinkEls.forEach((link, index) => {
      const handleParentActivate = () => {
        toggleChildNav(index);
        const hasMegaMenu = JSON.parse(link.dataset.navigationParentDt);
        toggleMegaMenu(hasMegaMenu, link, childNavEls[index]);

        if (link.dataset.navigationHasCtaCards === 'true') {
          navigationBanners?.forEach(banner => banner.classList.add('hidden'));
          navigationCTAs?.classList.add('hidden');
          navigationWrapperEl.classList.add('flex!');
        } else {
          navigationBanners?.forEach(banner => banner.classList.remove('hidden'));
          navigationCTAs?.classList.remove('hidden');
          navigationWrapperEl.classList.remove('flex!');
        }
      };

      link.addEventListener('mouseover', handleParentActivate);
      link.addEventListener('focusin', handleParentActivate);

      // Keyboard entry into the mega menu panel: Enter/Space/ArrowDown on a
      // parent button opens the panel (handleParentActivate) and moves focus
      // to its first link, so keyboard users can reach the submenu items.
      if (JSON.parse(link.dataset.navigationParentDt) !== true) return;

      link.addEventListener('keydown', (evt) => {
        if (!['Enter', ' ', 'Spacebar', 'ArrowDown'].includes(evt.key)) return;

        evt.preventDefault();
        handleParentActivate();
        focusFirstPanelLink(childNavEls[index]);
      });
    });

    if (childNavDtLinkEls.length) {
      childNavDtLinkEls.forEach(link => {
        const handleChildActivate = () => {
          toggleChildNavDt(link.dataset.childNavDtItem);
        };

        link.addEventListener('mouseover', handleChildActivate);
        link.addEventListener('focusin', handleChildActivate);
      });
    }
  };

  navToggle.addEventListener('click', () => {
    const isOpen = navToggle.classList.toggle('nav-open');
    navToggle.setAttribute(ARIAS.EXPANDED, isOpen);
    navToggleText.textContent = isOpen ? navToggleText.dataset.navToggleCloseText : navToggleText.dataset.navToggleText;
    navigationEl.classList.toggle(CLASSES.TRANSLATE_X_0);
    navigationEl.setAttribute(ARIAS.EXPANDED, isOpen);
    headerEl.classList.toggle(CLASSES.BG_WHITE, isOpen || headerHadWhiteBackground);
    if (announcementBar) {
      announcementBar.classList.toggle(CLASSES.BG_WHITE, isOpen || announcementBarHadWhiteBackground);
    }
    isOpen ? resetNavigation() : null;
  });

  // Bind ALL navigation links (not just ones with data attributes)
  const allNavLinks = navigationEl.querySelectorAll('a.navigation-item');
  bindNavigationLinks(allNavLinks);
  initMobNavigationTabs();
  initDesktopNav();
  initSearch();
};

export default initHeader;
